import cn from 'clsx';
import { useRef } from 'react';
import { faUserCircle, faBars, faSearch } from '@fortawesome/pro-light-svg-icons';

import { useUI } from '@v2/shared/lib/ui';
import { useSite } from '@v2/shared/lib/site';
import { useCart } from '@v2/shared/lib/cart';
import { useUser } from '@v2/shared/lib/user';
import useToggle from '@v2/shared/lib/useToggle';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import useShowFixedHeader from '@v2/shared/lib/useShowFixedHeader';
import { useSale } from '@v2/shared/lib/megaSale';
import SearchForm from '@v2/shared/components/SearchForm';

import Logo from '../../Full/Navigation/Logo';
import LogoSymbol from '../../Full/Navigation/LogoSymbol';
import IconButton from '@v2/shared/components/IconButton';
import AccountDialog from '../../Full/Navigation/AccountDialog';
import SearchBar from '../../Full/Navigation/MobileNav/SearchBar';
import BackgroundOverlay from '../../Full/Navigation/MobileNav/BackgroundOverlay';
import DesktopNavIconButton from '../../Full/Navigation/DesktopNav/DesktopNavIconButton';
import DesktopSupportPopover from '../../Full/Navigation/DesktopNav/DesktopSupportPopover';
import useMenuItems from '../../Full/Navigation/useMenuItems';

import CartButton from '@v2/shared/Layouts/MegaSale/CartButton';
import SaleCategoriesDialog from '@v2/shared/Layouts/MegaSale/SaleCategoriesDialog';
import { SaleSearchProps } from '@v2/shared/Layouts/MegaSale/types';

import '@v2/shared/lib/fontAwesome';

type Props = {
  searchProps: SaleSearchProps;
};

/**
 * The sale page's header.
 *
 * Full's header with three things changed, and nothing else — the brief was "the normal
 * header, minus the categories", so the differences are worth naming:
 *
 *   1. No category bar, on either width. A sale has its own sections and the site's
 *      categories lead away from it; support and account stay exactly where they are.
 *   2. The search box searches *this sale* rather than the site. Same component, same
 *      keyboard behaviour, different suggestions — see useSaleSearch.
 *   3. The cart icon opens the drawer instead of navigating to /cart, and the mobile
 *      hamburger opens this sale's sections.
 *
 * Losing the category bar is why the desktop bar is one row at every width above `md`
 * rather than Full's two: the second row existed to hold the categories that no longer
 * render, and keeping it would reserve 56px of empty white.
 */
/**
 * What the search box says, on both widths.
 *
 * The legacy page said "Search this Promotion" for a reason: this box does not search the
 * site, and an unqualified "Search..." invites a title that is not on the page and can
 * only ever come back empty.
 */
const SEARCH_PLACEHOLDER = 'Search this promotion...';

export default function Navigation({ searchProps }: Props) {
  const { country } = useSite();
  // The count is not known until the cart request answers — see Full's Navigation.
  const { cartCount: loadedCartCount, loaded } = useCart();
  const { user } = useUser();
  const cartCount = loaded ? loadedCartCount : null;
  const { buildUrl } = useUrlGenerator();
  const { headerRef, headerHeight, searchBar: searchBarState } = useUI();
  const showFixedHeader = useShowFixedHeader(headerHeight);
  const { accountLinks } = useMenuItems();
  const accountMenuState = useToggle();
  const categoriesMenuState = useToggle();
  const { data, select, cartDrawer } = useSale();

  const desktopSearchRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);
  const homeUrl = buildUrl('/');
  const categories = data?.categories ?? [];

  return (
    <>
      {/*
        Reserves the fixed header's height, which is out of flow and reserves none of its
        own. The classes are the first-frame estimate and the inline value takes over once
        the header has been measured — see Full's Navigation for why min-height rather
        than height (postcss/beatLegacyImportant raises `height` to !important).

        Two states rather than Full's three: with no category bar there is no tablet row
        to wrap, so the header is the mobile height or the desktop one.
      */}
      <div
        className="min-h-[109px] md:min-h-[82px]"
        style={headerHeight ? { minHeight: `${headerHeight}px` } : undefined}
      ></div>

      <header ref={headerRef} className={cn('block top-0 inset-x-0 fixed z-[250]')}>
        <nav aria-label="Primary" className="bg-white md:hidden border-b-[1.5px] border-slate-200">
          <div className="flex items-stretch justify-between">
            <div className="flex flex-1 items-center">
              <IconButton
                icon={faBars}
                onClick={categoriesMenuState.open}
                ariaLabel="Sale categories"
                className="p-2"
              />
              {showFixedHeader && (
                <IconButton
                  icon={faSearch}
                  ariaLabel="Search this sale"
                  onClick={searchBarState.toggle}
                  className="p-2 pl-1"
                  iconClassName="text-xl"
                />
              )}
            </div>
            <div className="flex items-center">
              <a href={homeUrl} aria-label="Navigate home" className="inline-flex px-3 items-center">
                <LogoSymbol />
              </a>
            </div>
            <div className="flex flex-1 justify-end items-center">
              <IconButton icon={faUserCircle} className="p-2" onClick={accountMenuState.toggle} ariaLabel="Account" />
              <CartButton isMobile cartCount={cartCount} onClick={cartDrawer.open} />
            </div>
          </div>
          <SearchBar
            isOpen={!showFixedHeader ? true : searchBarState.isOpen}
            searchBarState={searchBarState}
            searchAutoCompleteProps={searchProps}
            placeholder={SEARCH_PLACEHOLDER}
            ref={mobileSearchRef}
          />
        </nav>

        <nav
          aria-label="Primary"
          className={cn(
            'bg-white hidden md:flex flex-col items-center justify-center border-b-[1.5px] border-slate-200',
            'h-[82px]'
          )}
        >
          <div className="flex items-center md:px-6 2xl:px-10 w-full md:h-[70px]">
            <div className="flex flex-grow items-center">
              <a
                href={homeUrl}
                className="cursor-pointer items-center hidden md:flex mb-[5px] px-2 py-2"
                aria-label="Navigate home"
              >
                <Logo country={country} size="sm" />
              </a>
              <div className="flex flex-grow justify-end items-center">
                <div className="flex w-full group relative mx-5 lg:ml-5 lg:mr-7 xl:max-w-sm">
                  <SearchForm
                    id="sale-search-desktop"
                    searchBarState={searchBarState}
                    ref={desktopSearchRef}
                    searchAutoCompleteProps={searchProps}
                    placeholder={SEARCH_PLACEHOLDER}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <DesktopSupportPopover />
                  <DesktopNavIconButton
                    icon={faUserCircle}
                    onClick={accountMenuState.toggle}
                    title={user && user.name.first_name ? `Hi, ${user.name.first_name}` : 'Account'}
                  />
                  <CartButton cartCount={cartCount} onClick={cartDrawer.open} />
                </div>
              </div>
            </div>
          </div>
        </nav>

        <AccountDialog
          isOpen={accountMenuState.isOpen}
          toggle={accountMenuState.toggle}
          close={accountMenuState.close}
          accountLinks={accountLinks}
          user={user}
        />
        <SaleCategoriesDialog
          isOpen={categoriesMenuState.isOpen}
          toggle={categoriesMenuState.toggle}
          close={categoriesMenuState.close}
          categories={categories}
          onSelect={(categoryId) => select(categoryId)}
        />
      </header>
      <BackgroundOverlay isOpen={searchBarState.isFocused} />
    </>
  );
}
